import { AUTH_MESSAGES } from '@fos/shared/contracts/identity';
import { ArrowLeft, CircleCheck, LinkIcon } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useResetPassword } from '../../../lib/auth';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { PasswordInput } from '../../../ui/Input';
import { AuthLayout } from '../components/AuthLayout';
import { PasswordChecklist } from '../components/PasswordChecklist';
import { passwordChecks, passwordProblem } from '../password-policy';

const backLink = (
  <Link
    to="/login"
    className="inline-flex items-center gap-1.5 rounded-xs font-medium text-fg-secondary hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
  >
    <ArrowLeft size={16} strokeWidth={1.75} aria-hidden />
    Wróć do logowania
  </Link>
);

/** Nowe hasło z linku w mailu: /reset-hasla?token=… (token 30 min, jednorazowy). */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Token przenosimy z adresu do stanu historii (nie zostaje w pasku adresu ani w nagłówku Referer).
  const [token] = useState(
    () => params.get('token')?.trim() || (location.state as { token?: string } | null)?.token || '',
  );
  useEffect(() => {
    if (params.has('token')) navigate(location.pathname, { replace: true, state: { token } });
  }, [params, navigate, location.pathname, token]);
  const reset = useResetPassword();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [done, setDone] = useState(false);

  const checks = useMemo(() => passwordChecks(password, { confirm }), [password, confirm]);
  const problem = passwordProblem(checks, password);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);
    if (problem) return;
    try {
      await reset.mutateAsync({ token, password });
      setDone(true);
    } catch (err) {
      const code = appErrorCode(err);
      const issues = validationIssues(err);
      const passwordIssue = issues.find((i) => i.path === 'password');
      if (code === 'VALIDATION_ERROR' && passwordIssue) {
        setFormError(passwordIssue.message);
      } else if (code === 'VALIDATION_ERROR') {
        setTokenInvalid(true);
      } else {
        setFormError(errorMessage(err));
      }
    }
  };

  if (done) {
    return (
      <AuthLayout title="Hasło zmienione">
        <div className="flex flex-col gap-4">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-success-subtle text-success">
            <CircleCheck size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-body-s text-fg-secondary" role="status">
            Hasło zmienione, zaloguj się. Ze względów bezpieczeństwa wylogowaliśmy Cię ze wszystkich urządzeń.
          </p>
          <Button asChild variant="primary" size="l" fullWidth>
            <Link to="/login" state={{ notice: 'passwordChanged' }}>
              Zaloguj się
            </Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (!token || tokenInvalid) {
    return (
      <AuthLayout title="Link nie działa" footer={backLink}>
        <div className="flex flex-col gap-4">
          <span className="inline-flex size-10 items-center justify-center rounded-full bg-surface text-fg-muted">
            <LinkIcon size={20} strokeWidth={1.75} aria-hidden />
          </span>
          <p className="text-body-s text-fg-secondary">
            {AUTH_MESSAGES.invalidResetToken} Każdy link działa raz i przez 30 minut. Wyślij nowy, aby ustawić hasło.
          </p>
          <Button asChild variant="primary" size="l" fullWidth>
            <Link to="/nie-pamietam-hasla">Wyślij nowy link</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Ustaw nowe hasło" description="Po zmianie zalogujesz się nowym hasłem." footer={backLink}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-label="Nowe hasło">
        <Field label="Nowe hasło" error={submitted && problem && problem !== 'Hasła się różnią' ? problem : null}>
          <PasswordInput
            name="new-password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-describedby="password-checks"
          />
        </Field>
        <Field label="Powtórz nowe hasło" error={submitted && problem === 'Hasła się różnią' ? problem : null}>
          <PasswordInput
            name="confirm-password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <PasswordChecklist id="password-checks" checks={checks} />
        {formError && <FormMessage>{formError}</FormMessage>}
        <Button type="submit" variant="primary" size="l" fullWidth loading={reset.isPending} className="mt-1">
          Ustaw nowe hasło
        </Button>
      </form>
    </AuthLayout>
  );
}
